import React, { useCallback, useEffect, useState } from "react";
import Header from "@/components/Header";
import { api, formatINR, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Bell, Clock, MessageCircle, CalendarDays, TrendingUp, CheckCircle2, XCircle, Info, Send, Trash2, Plus, Film, Paperclip, FileText, Image as ImageIcon, X, Pencil, Check, CheckCheck, Mic, Square, Images } from "lucide-react";

const STATUSES = ["pending", "contacted", "test_ride", "purchased", "rejected"];

const STATUS_META = {
  pending: "bg-white/10 text-white",
  contacted: "bg-yellow-500/10 text-yellow-400",
  test_ride: "bg-blue-500/10 text-blue-400",
  purchased: "bg-[#00FF66]/10 text-[#00FF66]",
  rejected: "bg-red-500/10 text-red-400",
};

const EVENT_ICONS = {
  enquiry_whatsapp: <MessageCircle className="w-4 h-4" />,
  enquiry_test_ride: <CalendarDays className="w-4 h-4" />,
  test_ride_booked: <CalendarDays className="w-4 h-4" />,
  test_ride_confirmed: <CheckCircle2 className="w-4 h-4" />,
  test_ride_completed: <CheckCircle2 className="w-4 h-4" />,
  test_ride_cancelled: <XCircle className="w-4 h-4" />,
  status_new: <Info className="w-4 h-4" />,
  status_contacted: <TrendingUp className="w-4 h-4" />,
  status_converted: <CheckCircle2 className="w-4 h-4" />,
  status_closed: <XCircle className="w-4 h-4" />,
};

export default function Admin() {
  const [analytics, setAnalytics] = useState(null);
  const [refs, setRefs] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [rides, setRides] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [notifications, setNotifications] = useState({ items: [], unread: 0 });
  const [timelineLead, setTimelineLead] = useState(null);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [contest, setContest] = useState(null);
  const [contestDialog, setContestDialog] = useState(false);
  const [contestForm, setContestForm] = useState({ name: "", prize1: 5000, prize2: 3000, prize3: 2000 });
  const [voucherAnalytics, setVoucherAnalytics] = useState(null);
  const [chatThreads, setChatThreads] = useState({ threads: [], unread: 0 });
  const [activeThread, setActiveThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const [chatTemplates, setChatTemplates] = useState([]);
  const [templatesDialog, setTemplatesDialog] = useState(false);
  const [tplForm, setTplForm] = useState({ id: null, label: "", text: "", category: "general" });
  const [tplCategory, setTplCategory] = useState("all");
  const [chatUploading, setChatUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const chatFileRef = React.useRef(null);
  const recorderRef = React.useRef(null);
  const recTimerRef = React.useRef(null);
  const recStartRef = React.useRef(0);
  const [reels, setReels] = useState([]);
  const [reelForm, setReelForm] = useState({ title: "", customer_name: "", model_name: "", city: "", quote: "", video_url: "", poster_url: "" });
  const [reelDialog, setReelDialog] = useState(false);
  const [dialogRef, setDialogRef] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [rewardAmt, setRewardAmt] = useState("");

  const load = useCallback(async () => {
    const [a, r, p, u, l, tr, rd, nt, ct, va, ch, rl, tpl] = await Promise.all([
      api.get("/admin/analytics"),
      api.get("/admin/referrals"),
      api.get("/admin/payouts"),
      api.get("/admin/users"),
      api.get("/admin/leads"),
      api.get("/admin/test-rides"),
      api.get("/admin/redemptions"),
      api.get("/admin/notifications"),
      api.get("/contest/current"),
      api.get("/admin/vouchers/analytics"),
      api.get("/admin/chat/threads"),
      api.get("/admin/reels"),
      api.get("/admin/chat/templates"),
    ]);
    setAnalytics(a.data);
    setRefs(r.data);
    setPayouts(p.data);
    setUsers(u.data);
    setLeads(l.data);
    setRides(tr.data);
    setRedemptions(rd.data);
    setNotifications(nt.data);
    setContest(ct.data);
    setVoucherAnalytics(va.data);
    setChatThreads(ch.data);
    setReels(rl.data);
    setChatTemplates(tpl.data);
    setContestForm({
      name: ct.data.contest.name,
      prize1: ct.data.contest.prizes[0] || 0,
      prize2: ct.data.contest.prizes[1] || 0,
      prize3: ct.data.contest.prizes[2] || 0,
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-refresh notifications every 20s
  useEffect(() => {
    const int = setInterval(() => {
      api.get("/admin/notifications").then((r) => {
        setNotifications((prev) => {
          if (r.data.unread > prev.unread && prev.unread !== undefined) {
            toast.success(`🔔 ${r.data.unread - prev.unread} new notification`);
          }
          return r.data;
        });
      }).catch((err) => console.error("notif poll failed", err));
      api.get("/admin/chat/threads").then((r) => setChatThreads(r.data)).catch((err) => console.error("chat threads poll failed", err));
    }, 20000);
    return () => clearInterval(int);
  }, []);

  // Auto-refresh active thread
  useEffect(() => {
    if (!activeThread) return;
    const int = setInterval(() => {
      api.get(`/admin/chat/thread/${activeThread}`).then((r) => setThreadMessages(r.data.messages || [])).catch((err) => console.error("thread poll failed", err));
    }, 8000);
    return () => clearInterval(int);
  }, [activeThread]);

  const openRefDialog = (r) => {
    setDialogRef(r);
    setNewStatus(r.status);
    setRewardAmt(r.reward_amount || "");
  };

  const updateRef = async () => {
    try {
      const body = { status: newStatus };
      if (newStatus === "purchased" && rewardAmt) body.reward_amount = parseFloat(rewardAmt);
      await api.patch(`/admin/referrals/${dialogRef.id}`, body);
      toast.success("Referral updated");
      setDialogRef(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const updatePayout = async (id, status) => {
    try {
      await api.patch(`/admin/payouts/${id}`, { status });
      toast.success(`Payout ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const updateLead = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success(`Lead → ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const updateRide = async (id, status) => {
    try {
      await api.patch(`/admin/test-rides/${id}`, { status });
      toast.success(`Test ride → ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const updateRedemption = async (id, status) => {
    try {
      await api.patch(`/admin/redemptions/${id}`, { status });
      toast.success(`Redemption ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const markAllRead = async () => {
    await api.post("/admin/notifications/read-all");
    load();
  };

  const openTimeline = async (lead) => {
    setTimelineLead(lead);
    try {
      const { data } = await api.get(`/admin/leads/${lead.id}/timeline`);
      setTimelineEvents(data.events || []);
    } catch (err) {
      setTimelineEvents([]);
    }
  };

  const saveContest = async () => {
    try {
      await api.patch("/admin/contest", {
        name: contestForm.name,
        prizes: [parseFloat(contestForm.prize1) || 0, parseFloat(contestForm.prize2) || 0, parseFloat(contestForm.prize3) || 0],
      });
      toast.success("Contest updated");
      setContestDialog(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openThread = async (thread_key) => {
    setActiveThread(thread_key);
    try {
      const { data } = await api.get(`/admin/chat/thread/${thread_key}`);
      setThreadMessages(data.messages || []);
      load();
    } catch (err) {
      console.error("Failed to open thread", err);
    }
  };

  const sendAdminChat = async (e, override) => {
    if (e && e.preventDefault) e.preventDefault();
    const payload = override || { text: chatText.trim() };
    if (!activeThread) return;
    if (!payload.text && !payload.attachment_url) return;
    try {
      const { data } = await api.post("/chat/send", { thread_key: activeThread, ...payload });
      setThreadMessages((m) => [...m, data]);
      if (!override) setChatText("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sendTemplate = (tpl) => {
    sendAdminChat(null, { text: tpl.text });
  };

  const uploadChatAttachment = async (file) => {
    if (!file || !activeThread) return;
    setChatUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/chat/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      await sendAdminChat(null, {
        text: chatText.trim(),
        attachment_url: data.url,
        attachment_type: data.type,
        attachment_name: data.name,
      });
      setChatText("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setChatUploading(false);
      if (chatFileRef.current) chatFileRef.current.value = "";
    }
  };

  const saveTemplate = async () => {
    const label = tplForm.label.trim();
    const text = tplForm.text.trim();
    const category = tplForm.category || "general";
    if (!label || !text) { toast.error("Label and text are required"); return; }
    try {
      if (tplForm.id) {
        await api.patch(`/admin/chat/templates/${tplForm.id}`, { label, text, category });
        toast.success("Template updated");
      } else {
        await api.post("/admin/chat/templates", { label, text, category });
        toast.success("Template added");
      }
      setTplForm({ id: null, label: "", text: "", category });
      const { data } = await api.get("/admin/chat/templates");
      setChatTemplates(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/admin/chat/templates/${id}`);
      const { data } = await api.get("/admin/chat/templates");
      setChatTemplates(data);
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const attachmentSrc = (url) => {
    const token = localStorage.getItem("sse_token");
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}${url}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
  };

  const TEMPLATE_CATEGORIES = [
    { key: "test_ride", label: "Test Ride" },
    { key: "payout", label: "Payout" },
    { key: "kyc", label: "KYC" },
    { key: "delivery", label: "Delivery" },
    { key: "general", label: "General" },
  ];

  const uploadBlob = async (blob, filename, mime, extraMeta = {}) => {
    const form = new FormData();
    form.append("file", blob, filename);
    const { data } = await api.post("/chat/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
    await sendAdminChat(null, {
      text: chatText.trim(),
      attachment_url: data.url,
      attachment_type: data.type,
      attachment_name: data.name,
      ...extraMeta,
    });
    setChatText("");
  };

  const startRecording = async () => {
    if (!activeThread) { toast.error("Open a thread first"); return; }
    if (!navigator.mediaDevices?.getUserMedia) { toast.error("Voice recording not supported in this browser"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(recTimerRef.current);
        const duration = (Date.now() - recStartRef.current) / 1000;
        setRecording(false);
        setRecSeconds(0);
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        if (blob.size === 0) return;
        try {
          setChatUploading(true);
          await uploadBlob(blob, `voice-${Date.now()}.webm`, blob.type, { attachment_duration: duration });
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Voice send failed");
        } finally {
          setChatUploading(false);
        }
      };
      recorderRef.current = rec;
      recStartRef.current = Date.now();
      rec.start();
      setRecording(true);
      setRecSeconds(0);
      recTimerRef.current = setInterval(() => {
        const s = Math.floor((Date.now() - recStartRef.current) / 1000);
        setRecSeconds(s);
        if (s >= 180) { stopRecording(); }
      }, 500);
    } catch (err) {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch (err) { console.error("stopRecording failed", err); }
  };

  const cancelRecording = () => {
    try {
      const rec = recorderRef.current;
      if (rec) {
        rec.ondataavailable = null;
        rec.onstop = () => {
          rec.stream.getTracks().forEach((t) => t.stop());
        };
        rec.stop();
      }
    } catch (err) { console.error("cancelRecording failed", err); }
    clearInterval(recTimerRef.current);
    setRecording(false);
    setRecSeconds(0);
  };

  const threadAttachments = threadMessages.filter((m) => m.attachment_url);

  const addReel = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/reels", reelForm);
      toast.success("Reel added");
      setReelForm({ title: "", customer_name: "", model_name: "", city: "", quote: "", video_url: "", poster_url: "" });
      setReelDialog(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteReel = async (id) => {
    if (!window.confirm("Delete this reel?")) return;
    await api.delete(`/admin/reels/${id}`);
    toast.success("Reel deleted");
    load();
  };

  if (!analytics) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Header /><div className="max-w-7xl mx-auto p-10 text-white/50">Loading admin panel…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise">
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-2">Admin Console</div>
            <h1 className="font-display font-black text-3xl lg:text-4xl">SSE Referral Program Control</h1>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="relative w-11 h-11 rounded-full border border-white/15 bg-[#111] hover:border-[#00FF66]/50 flex items-center justify-center transition-colors"
                data-testid="notification-bell"
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5 text-white" />
                {notifications.unread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-[#00FF66] text-black text-[10px] font-black flex items-center justify-center px-1" data-testid="notification-count">
                    {notifications.unread > 9 ? "9+" : notifications.unread}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-96 bg-[#111] border-white/10 p-0 max-h-[70vh] overflow-hidden flex flex-col">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.2em] text-[#00FF66] font-bold">Alerts</div>
                {notifications.unread > 0 && (
                  <button onClick={markAllRead} className="text-xs text-white/50 hover:text-[#00FF66]" data-testid="mark-all-read">
                    Mark all read
                  </button>
                )}
              </div>
              <div className="overflow-y-auto flex-1">
                {notifications.items.length === 0 ? (
                  <div className="p-8 text-center text-white/40 text-sm">No alerts yet.</div>
                ) : notifications.items.map((n) => (
                  <div
                    key={n.id}
                    className={`p-4 border-b border-white/5 ${!n.read ? "bg-[#00FF66]/[0.03]" : ""}`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-1.5 h-1.5 rounded-full mt-2 ${!n.read ? "bg-[#00FF66]" : "bg-white/20"}`} />
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-white">{n.title}</div>
                        <div className="text-xs text-white/60 mt-1 leading-relaxed">{n.body}</div>
                        <div className="text-[10px] text-white/40 mt-2 flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          {new Date(n.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                          {n.sent_whatsapp && <Badge className="bg-[#25D366]/10 text-[#25D366] border-transparent ml-auto text-[9px] py-0 px-1.5">WA sent</Badge>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-white/10 bg-black/40">
                <div className="text-[10px] text-white/40 leading-relaxed">
                  💡 Enable auto-WhatsApp: message <span className="text-white/70 font-mono">"I allow callmebot to send me messages"</span> to <span className="text-white/70 font-mono">+34 644 51 76 92</span>, then add the API key to <span className="text-white/70 font-mono">CALLMEBOT_APIKEY</span> in backend .env
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Analytics */}
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {[
            { label: "Users", v: analytics.total_users, testId: "adm-users" },
            { label: "Referrals", v: analytics.total_referrals, testId: "adm-refs" },
            { label: "Conversions", v: `${analytics.conversions}`, testId: "adm-conv" },
            { label: "New Leads", v: analytics.leads_new ?? 0, testId: "adm-leads", highlight: (analytics.leads_new ?? 0) > 0 },
            { label: "Test Rides", v: analytics.test_rides_pending ?? 0, testId: "adm-rides", highlight: (analytics.test_rides_pending ?? 0) > 0 },
            { label: "Rewards", v: formatINR(analytics.total_rewards_awarded), testId: "adm-rewards" },
          ].map((s) => (
            <Card key={s.label} className={`bg-[#111] border-white/10 p-4 ${s.highlight ? "border-[#00FF66]/40" : ""}`} data-testid={s.testId}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">{s.label}</div>
              <div className={`font-display font-black text-2xl mt-2 ${s.highlight ? "text-[#00FF66]" : "text-white"}`}>{s.v}</div>
            </Card>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Card className="bg-[#111] border-white/10 p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-bold">Payouts Pending</div>
            <div className="font-display font-black text-3xl mt-3 text-yellow-400">{formatINR(analytics.payouts_pending)}</div>
          </Card>
          <Card className="bg-[#111] border-white/10 p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-bold">Payouts Paid</div>
            <div className="font-display font-black text-3xl mt-3 text-[#00FF66]">{formatINR(analytics.payouts_paid)}</div>
          </Card>
        </div>

        {contest?.contest && (
          <Card className="bg-gradient-to-r from-[#0F1A0F] to-[#111] border-[#00FF66]/30 p-5 mb-8 relative overflow-hidden" data-testid="admin-contest-strip">
            <div className="flex flex-wrap items-center justify-between gap-3 relative">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold mb-1">Contest · Live</div>
                <div className="font-display font-black text-xl">{contest.contest.name}</div>
                <div className="text-xs text-white/60 mt-1">
                  Prizes: {contest.contest.prizes.map((p) => formatINR(p)).join(" · ")} · {contest.top.length} qualifying referrers
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/admin/digest/run");
                      toast.success(`Sent digest to ${data.users_notified} SSEs`);
                      load();
                    } catch (err) {
                      toast.error(formatApiError(err.response?.data?.detail));
                    }
                  }}
                  variant="outline"
                  className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white h-9 text-xs"
                  data-testid="admin-send-digest"
                >
                  Send Weekly Digest
                </Button>
                <Button
                  onClick={() => setContestDialog(true)}
                  variant="outline"
                  className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white h-9 text-xs"
                  data-testid="admin-contest-edit"
                >
                  Edit prizes
                </Button>
              </div>
            </div>
          </Card>
        )}


        <Tabs defaultValue="leads" className="space-y-6">
          <TabsList className="bg-[#111] border border-white/10 p-1 flex-wrap h-auto">
            <TabsTrigger value="leads" data-testid="admin-tab-leads" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-black">Leads</TabsTrigger>
            <TabsTrigger value="chat" data-testid="admin-tab-chat" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-black">
              Chat {chatThreads.unread > 0 && <span className="ml-1.5 text-[10px] font-mono bg-red-500 text-white rounded-full px-1.5 py-0.5">{chatThreads.unread}</span>}
            </TabsTrigger>
            <TabsTrigger value="rides" data-testid="admin-tab-rides" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-black">Test Rides</TabsTrigger>
            <TabsTrigger value="referrals" data-testid="admin-tab-referrals" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-black">Referrals</TabsTrigger>
            <TabsTrigger value="vouchers" data-testid="admin-tab-vouchers" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-black">Vouchers</TabsTrigger>
            <TabsTrigger value="redemptions" data-testid="admin-tab-redemptions" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-black">Redemptions</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="admin-tab-payouts" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-black">Payouts</TabsTrigger>
            <TabsTrigger value="reels" data-testid="admin-tab-reels" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-black">Reels</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users" className="data-[state=active]:bg-[#00FF66] data-[state=active]:text-black">Users</TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            <Card className="bg-[#111] border-white/10 overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-[0.2em] text-white/40 font-bold border-b border-white/5">
                <div className="col-span-3">Model</div>
                <div className="col-span-2">Source</div>
                <div className="col-span-2">Contact</div>
                <div className="col-span-2">When</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {leads.length === 0 ? (
                <div className="p-10 text-center text-white/40 text-sm" data-testid="admin-no-leads">No leads yet. Every WhatsApp Enquire tap on the site lands here.</div>
              ) : leads.map((l) => (
                <div key={l.id} onClick={() => openTimeline(l)} className="grid grid-cols-12 px-4 py-3 border-b border-white/5 last:border-0 items-center text-sm hover:bg-white/[0.02] cursor-pointer" data-testid={`admin-lead-row-${l.id}`}>
                  <div className="col-span-3 font-medium flex items-center gap-2">
                    <span className="text-white/40">→</span>
                    {l.model_name}
                  </div>
                  <div className="col-span-2">
                    <Badge className={l.source === "test_ride" ? "bg-blue-500/10 text-blue-400 border-transparent" : "bg-[#25D366]/10 text-[#25D366] border-transparent"}>
                      {l.source}
                    </Badge>
                  </div>
                  <div className="col-span-2 text-xs text-white/70">
                    {l.name || <span className="italic text-white/40">Anonymous</span>}
                    {l.phone && <div className="text-white/50">{l.phone}</div>}
                  </div>
                  <div className="col-span-2 text-xs text-white/50">
                    {new Date(l.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="col-span-1">
                    <Badge className={
                      l.status === "converted" ? "bg-[#00FF66]/10 text-[#00FF66] border-transparent" :
                      l.status === "contacted" ? "bg-yellow-500/10 text-yellow-400 border-transparent" :
                      l.status === "closed" ? "bg-red-500/10 text-red-400 border-transparent" :
                      "bg-white/10 text-white border-transparent"
                    }>{l.status}</Badge>
                  </div>
                  <div className="col-span-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <Select value={l.status} onValueChange={(v) => updateLead(l.id, v)}>
                      <SelectTrigger className="bg-black/40 border-white/10 h-8 text-xs w-32 ml-auto" data-testid={`lead-status-${l.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111] border-white/10 text-white">
                        {["new", "contacted", "converted", "closed"].map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </Card>
          </TabsContent>


          <TabsContent value="chat">
            <div className="grid lg:grid-cols-3 gap-4" style={{ minHeight: 500 }}>
              {/* Threads list */}
              <Card className="bg-[#111] border-white/10 overflow-hidden lg:col-span-1 flex flex-col">
                <div className="p-4 border-b border-white/5">
                  <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold">Conversations</div>
                  <div className="text-[10px] text-white/40 mt-1">{chatThreads.threads.length} threads · {chatThreads.unread} unread</div>
                </div>
                <div className="overflow-y-auto flex-1 max-h-[520px]">
                  {chatThreads.threads.length === 0 ? (
                    <div className="p-8 text-center text-white/40 text-sm" data-testid="chat-no-threads">
                      No chats yet. SSE staff will start conversations from their dashboard.
                    </div>
                  ) : chatThreads.threads.map((t) => (
                    <button
                      key={t.thread_key}
                      onClick={() => openThread(t.thread_key)}
                      className={`w-full text-left p-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors ${activeThread === t.thread_key ? "bg-[#00FF66]/[0.05]" : ""}`}
                      data-testid={`chat-thread-${t.thread_key}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate flex items-center gap-2">
                            {t.user_name}
                            <span className="text-[10px] uppercase tracking-wider text-white/40">{t.user_role}</span>
                          </div>
                          <div className="text-xs text-white/50 truncate mt-0.5">
                            {t.last_role === "admin" ? "You: " : ""}{t.last_message}
                          </div>
                          <div className="text-[10px] text-white/30 mt-1">
                            {new Date(t.last_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </div>
                        </div>
                        {t.unread > 0 && (
                          <span className="min-w-[20px] h-5 rounded-full bg-[#00FF66] text-black text-[10px] font-black flex items-center justify-center px-1 flex-shrink-0">{t.unread}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </Card>
              {/* Thread view */}
              <Card className="bg-[#111] border-white/10 overflow-hidden lg:col-span-2 flex flex-col">
                {!activeThread ? (
                  <div className="flex-1 flex items-center justify-center text-white/40 text-sm p-10 text-center">
                    <div>
                      <MessageCircle className="w-8 h-8 mx-auto text-white/20 mb-3" />
                      Select a conversation to start replying
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="p-4 border-b border-white/10 bg-black/40 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">
                          {chatThreads.threads.find((t) => t.thread_key === activeThread)?.user_name || "Chat"}
                        </div>
                        <div className="text-[10px] text-white/40">
                          {chatThreads.threads.find((t) => t.thread_key === activeThread)?.user_phone}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          onClick={() => setGalleryOpen(true)}
                          className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white h-8 text-[11px] px-3"
                          data-testid="chat-gallery-btn"
                        >
                          <Images className="w-3 h-3 mr-1.5" /> Files
                          {threadAttachments.length > 0 && (
                            <span className="ml-1.5 min-w-[16px] h-4 rounded-full bg-[#00FF66]/20 text-[#00FF66] text-[9px] font-black flex items-center justify-center px-1">
                              {threadAttachments.length}
                            </span>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => { setTplForm({ id: null, label: "", text: "", category: "general" }); setTemplatesDialog(true); }}
                          className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white h-8 text-[11px] px-3"
                          data-testid="manage-templates-btn"
                        >
                          <Pencil className="w-3 h-3 mr-1.5" /> Templates
                        </Button>
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-2.5 max-h-[420px]">
                      {threadMessages.map((m) => (
                        <div key={m.id} className={`flex ${m.from_role === "admin" ? "justify-end" : "justify-start"}`} data-testid={`admin-chat-msg-${m.id}`}>
                          <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                            m.from_role === "admin"
                              ? "bg-[#00FF66] text-black rounded-br-sm"
                              : "bg-white/5 text-white border border-white/10 rounded-bl-sm"
                          }`}>
                            {m.attachment_url && m.attachment_type === "image" && (
                              <a href={attachmentSrc(m.attachment_url)} target="_blank" rel="noreferrer" data-testid={`admin-chat-img-${m.id}`}>
                                <img
                                  src={attachmentSrc(m.attachment_url)}
                                  alt={m.attachment_name || "attachment"}
                                  className="rounded-lg max-w-[240px] max-h-[240px] object-cover mb-1.5 border border-black/10"
                                />
                              </a>
                            )}
                            {m.attachment_url && m.attachment_type === "audio" && (
                              <audio
                                src={attachmentSrc(m.attachment_url)}
                                controls
                                preload="metadata"
                                className="mb-1.5 max-w-[240px]"
                                data-testid={`admin-chat-audio-${m.id}`}
                              />
                            )}
                            {m.attachment_url && m.attachment_type === "file" && (
                              <a
                                href={attachmentSrc(m.attachment_url)}
                                target="_blank"
                                rel="noreferrer"
                                className={`flex items-center gap-2 mb-1.5 px-2 py-1.5 rounded-lg text-xs font-medium ${m.from_role === "admin" ? "bg-black/10 text-black hover:bg-black/15" : "bg-white/10 text-white hover:bg-white/15"}`}
                                data-testid={`admin-chat-file-${m.id}`}
                              >
                                <FileText className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate max-w-[180px]">{m.attachment_name || "Attachment"}</span>
                              </a>
                            )}
                            {m.text && <div className="leading-snug whitespace-pre-wrap break-words">{m.text}</div>}
                            <div className={`text-[10px] mt-1 flex items-center gap-1 opacity-70 ${m.from_role === "admin" ? "text-black/60 justify-end" : "text-white/50"}`}>
                              <span>{new Date(m.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
                              {m.from_role === "admin" && (
                                m.read_by_user ? (
                                  <CheckCheck className="w-3.5 h-3.5 text-blue-700" data-testid={`admin-chat-read-${m.id}`} />
                                ) : (
                                  <Check className="w-3.5 h-3.5 text-black/50" data-testid={`admin-chat-sent-${m.id}`} />
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {chatTemplates.length > 0 && (
                      <div className="border-t border-white/10 bg-black/20">
                        <div className="px-3 pt-2 pb-1 flex gap-1 overflow-x-auto" data-testid="chat-template-cats">
                          <button
                            onClick={() => setTplCategory("all")}
                            className={`whitespace-nowrap text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 rounded-full transition-colors ${tplCategory === "all" ? "bg-[#00FF66] text-black" : "text-white/50 hover:text-white"}`}
                            data-testid="chat-template-cat-all"
                          >
                            All
                          </button>
                          {TEMPLATE_CATEGORIES.map((c) => {
                            const has = chatTemplates.some((t) => (t.category || "general") === c.key);
                            if (!has) return null;
                            return (
                              <button
                                key={c.key}
                                onClick={() => setTplCategory(c.key)}
                                className={`whitespace-nowrap text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 rounded-full transition-colors ${tplCategory === c.key ? "bg-[#00FF66] text-black" : "text-white/50 hover:text-white"}`}
                                data-testid={`chat-template-cat-${c.key}`}
                              >
                                {c.label}
                              </button>
                            );
                          })}
                        </div>
                        <div className="px-3 pb-2 overflow-x-auto" data-testid="chat-template-chips">
                          <div className="flex gap-2 min-w-max">
                            {chatTemplates
                              .filter((t) => tplCategory === "all" || (t.category || "general") === tplCategory)
                              .map((t) => (
                                <button
                                  key={t.id}
                                  onClick={() => sendTemplate(t)}
                                  className="rounded-full border border-[#00FF66]/30 bg-[#00FF66]/5 hover:bg-[#00FF66]/15 text-[#00FF66] text-[11px] font-semibold px-3 py-1.5 whitespace-nowrap transition-colors"
                                  data-testid={`chat-template-chip-${t.id}`}
                                  title={t.text}
                                >
                                  {t.label}
                                </button>
                              ))}
                          </div>
                        </div>
                      </div>
                    )}
                    <form onSubmit={sendAdminChat} className="p-3 border-t border-white/10 bg-black/40 flex items-center gap-2">
                      <input
                        type="file"
                        ref={chatFileRef}
                        onChange={(e) => uploadChatAttachment(e.target.files?.[0])}
                        accept="image/png,image/jpeg,image/webp,application/pdf"
                        className="hidden"
                        data-testid="admin-chat-file-input"
                      />
                      {recording ? (
                        <div className="flex-1 flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-3 h-10" data-testid="admin-recording-bar">
                          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                          <span className="text-xs text-white font-mono flex-1">
                            Recording · {String(Math.floor(recSeconds / 60)).padStart(2, "0")}:{String(recSeconds % 60).padStart(2, "0")}
                          </span>
                          <button type="button" onClick={cancelRecording} className="text-white/60 hover:text-white text-xs" data-testid="admin-recording-cancel">Cancel</button>
                          <button
                            type="button"
                            onClick={stopRecording}
                            className="w-8 h-8 rounded-full bg-[#00FF66] text-black flex items-center justify-center hover:bg-[#00E55C]"
                            data-testid="admin-recording-stop"
                            aria-label="Stop and send"
                          >
                            <Send className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => chatFileRef.current?.click()}
                            disabled={chatUploading}
                            className="w-10 h-10 rounded-full border border-white/15 bg-black/40 hover:border-[#00FF66]/60 flex items-center justify-center text-white/70 hover:text-[#00FF66] transition-colors disabled:opacity-50"
                            data-testid="admin-chat-attach"
                            aria-label="Attach file"
                          >
                            {chatUploading ? <span className="text-[10px] font-mono">…</span> : <Paperclip className="w-4 h-4" />}
                          </button>
                          <button
                            type="button"
                            onClick={startRecording}
                            disabled={chatUploading}
                            className="w-10 h-10 rounded-full border border-white/15 bg-black/40 hover:border-[#00FF66]/60 flex items-center justify-center text-white/70 hover:text-[#00FF66] transition-colors disabled:opacity-50"
                            data-testid="admin-chat-mic"
                            aria-label="Record voice note"
                          >
                            <Mic className="w-4 h-4" />
                          </button>
                          <Input
                            value={chatText}
                            onChange={(e) => setChatText(e.target.value)}
                            placeholder="Type a reply…"
                            className="flex-1 bg-black/40 border-white/10 rounded-full h-10"
                            data-testid="admin-chat-input"
                          />
                          <Button type="submit" disabled={!chatText.trim()} className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold h-10 w-10 p-0" data-testid="admin-chat-send">
                            <Send className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </form>
                  </>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="rides">
            <Card className="bg-[#111] border-white/10 overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-[0.2em] text-white/40 font-bold border-b border-white/5">
                <div className="col-span-3">Customer</div>
                <div className="col-span-3">Model</div>
                <div className="col-span-2">Slot</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {rides.length === 0 ? (
                <div className="p-10 text-center text-white/40 text-sm" data-testid="admin-no-rides">No test rides booked yet.</div>
              ) : rides.map((r) => (
                <div key={r.id} className="grid grid-cols-12 px-4 py-3 border-b border-white/5 last:border-0 items-center text-sm" data-testid={`admin-ride-row-${r.id}`}>
                  <div className="col-span-3">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-white/50">{r.phone}</div>
                  </div>
                  <div className="col-span-3 text-white/80">{r.model_name}</div>
                  <div className="col-span-2 text-xs">
                    <div className="text-white">{r.preferred_date}</div>
                    <div className="text-white/50">{r.preferred_time}</div>
                  </div>
                  <div className="col-span-2">
                    <Badge className={
                      r.status === "confirmed" ? "bg-blue-500/10 text-blue-400 border-transparent" :
                      r.status === "completed" ? "bg-[#00FF66]/10 text-[#00FF66] border-transparent" :
                      r.status === "cancelled" ? "bg-red-500/10 text-red-400 border-transparent" :
                      "bg-yellow-500/10 text-yellow-400 border-transparent"
                    }>{r.status}</Badge>
                  </div>
                  <div className="col-span-2 text-right space-x-1">
                    {r.status === "requested" && (
                      <Button size="sm" variant="outline" onClick={() => updateRide(r.id, "confirmed")} className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white h-8 text-xs" data-testid={`ride-confirm-${r.id}`}>Confirm</Button>
                    )}
                    {r.status === "confirmed" && (
                      <Button size="sm" onClick={() => updateRide(r.id, "completed")} className="bg-[#00FF66] text-black hover:bg-[#00E55C] h-8 text-xs" data-testid={`ride-complete-${r.id}`}>Complete</Button>
                    )}
                    {(r.status === "requested" || r.status === "confirmed") && (
                      <Button size="sm" variant="outline" onClick={() => updateRide(r.id, "cancelled")} className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 h-8 text-xs" data-testid={`ride-cancel-${r.id}`}>Cancel</Button>
                    )}
                  </div>
                </div>
              ))}
            </Card>
          </TabsContent>

          <TabsContent value="referrals">
            <Card className="bg-[#111] border-white/10 overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-[0.2em] text-white/40 font-bold border-b border-white/5">
                <div className="col-span-2">Referrer</div>
                <div className="col-span-2">Referee</div>
                <div className="col-span-2">City</div>
                <div className="col-span-2">Model</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {refs.length === 0 ? (
                <div className="p-10 text-center text-white/40 text-sm" data-testid="admin-no-refs">No referrals yet.</div>
              ) : refs.map((r) => (
                <div key={r.id} className="grid grid-cols-12 px-4 py-3 border-b border-white/5 last:border-0 items-center text-sm" data-testid={`admin-ref-row-${r.id}`}>
                  <div className="col-span-2">
                    <div className="font-medium">{r.referrer_name}</div>
                    <div className="text-xs text-white/40 uppercase tracking-wider">{r.referrer_role}</div>
                  </div>
                  <div className="col-span-2">
                    <div>{r.referee_name}</div>
                    <div className="text-xs text-white/40">{r.referee_phone}</div>
                  </div>
                  <div className="col-span-2 text-white/70">{r.city}</div>
                  <div className="col-span-2 text-white/70">{r.interested_model}</div>
                  <div className="col-span-2">
                    <Badge className={`${STATUS_META[r.status]} border-transparent`}>{r.status}</Badge>
                  </div>
                  <div className="col-span-2 text-right">
                    <Button size="sm" variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white" onClick={() => openRefDialog(r)} data-testid={`admin-update-ref-${r.id}`}>Update</Button>
                  </div>
                </div>
              ))}
            </Card>
          </TabsContent>

          <TabsContent value="redemptions">
            <Card className="bg-[#111] border-white/10 overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-[0.2em] text-white/40 font-bold border-b border-white/5">
                <div className="col-span-3">User</div>
                <div className="col-span-4">Reward</div>
                <div className="col-span-1">Cost</div>
                <div className="col-span-2">Date</div>
                <div className="col-span-2 text-right">Status / Actions</div>
              </div>
              {redemptions.length === 0 ? (
                <div className="p-10 text-center text-white/40 text-sm" data-testid="admin-no-redemptions">
                  No redemption requests yet.
                </div>
              ) : redemptions.map((r) => (
                <div key={r.id} className="grid grid-cols-12 px-4 py-3 border-b border-white/5 last:border-0 items-center text-sm" data-testid={`admin-redemption-${r.id}`}>
                  <div className="col-span-3 font-medium">{r.user_name}</div>
                  <div className="col-span-4">{r.reward_name}</div>
                  <div className="col-span-1 font-mono text-[#00FF66]">{r.cost}</div>
                  <div className="col-span-2 text-xs text-white/50">
                    {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                  <div className="col-span-2 text-right space-x-1">
                    {r.status === "requested" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => updateRedemption(r.id, "approved")} className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white h-8 text-xs" data-testid={`redemption-approve-${r.id}`}>Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => updateRedemption(r.id, "rejected")} className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 h-8 text-xs" data-testid={`redemption-reject-${r.id}`}>Reject</Button>
                      </>
                    )}
                    {r.status === "approved" && (
                      <Button size="sm" onClick={() => updateRedemption(r.id, "fulfilled")} className="bg-[#00FF66] text-black hover:bg-[#00E55C] h-8 text-xs" data-testid={`redemption-fulfill-${r.id}`}>Mark Fulfilled</Button>
                    )}
                    {(r.status === "fulfilled" || r.status === "rejected") && (
                      <Badge className={
                        r.status === "fulfilled" ? "bg-[#00FF66]/10 text-[#00FF66] border-transparent" :
                        "bg-red-500/10 text-red-400 border-transparent"
                      }>{r.status}</Badge>
                    )}
                  </div>
                </div>
              ))}
            </Card>
          </TabsContent>



          <TabsContent value="vouchers">
            {voucherAnalytics && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-4 gap-4">
                  {[
                    { label: "Issued", v: voucherAnalytics.total_issued, testId: "va-issued" },
                    { label: "Redeemed", v: voucherAnalytics.total_redeemed, testId: "va-redeemed", highlight: true },
                    { label: "Redemption Rate", v: `${voucherAnalytics.redemption_rate}%`, testId: "va-rate" },
                    { label: "Discount Given", v: formatINR(voucherAnalytics.total_discount_given), testId: "va-discount" },
                  ].map((s) => (
                    <Card key={s.label} className={`bg-[#111] border-white/10 p-5 ${s.highlight ? "border-[#00FF66]/40" : ""}`} data-testid={s.testId}>
                      <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-bold">{s.label}</div>
                      <div className={`font-display font-black text-3xl mt-2 ${s.highlight ? "text-[#00FF66]" : "text-white"}`}>{s.v}</div>
                    </Card>
                  ))}
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Top referrers */}
                  <Card className="bg-[#111] border-white/10 overflow-hidden">
                    <div className="p-6 border-b border-white/5">
                      <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold">Top Voucher Converters</div>
                    </div>
                    {voucherAnalytics.top_referrers.length === 0 ? (
                      <div className="p-8 text-center text-white/40 text-sm" data-testid="va-no-top">No voucher redemptions yet.</div>
                    ) : voucherAnalytics.top_referrers.map((r, idx) => (
                      <div key={r.user_id || idx} className="grid grid-cols-12 px-6 py-3 border-b border-white/5 last:border-0 items-center text-sm" data-testid={`va-top-row-${idx}`}>
                        <div className={`col-span-1 font-display font-black text-lg ${idx < 3 ? "text-[#00FF66]" : "text-white/40"}`}>#{idx + 1}</div>
                        <div className="col-span-6">
                          <div className="font-medium">{r.name}</div>
                          <div className="text-xs text-white/40 font-mono">{r.referral_code}</div>
                        </div>
                        <div className="col-span-2 text-right font-mono">{r.count}</div>
                        <div className="col-span-3 text-right font-display font-bold text-[#00FF66]">{formatINR(r.discount_given)}</div>
                      </div>
                    ))}
                  </Card>

                  {/* By month */}
                  <Card className="bg-[#111] border-white/10 overflow-hidden">
                    <div className="p-6 border-b border-white/5">
                      <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold">Monthly Trend</div>
                    </div>
                    {voucherAnalytics.by_month.length === 0 ? (
                      <div className="p-8 text-center text-white/40 text-sm">No data yet.</div>
                    ) : voucherAnalytics.by_month.map((m) => (
                      <div key={m.month_key} className="px-6 py-4 border-b border-white/5 last:border-0" data-testid={`va-month-${m.month_key}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-mono text-sm text-white">{m.month_key}</div>
                          <div className="text-xs text-white/60">{m.redeemed} / {m.issued} · <span className="text-[#00FF66] font-bold">{m.rate}%</span></div>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-[#00FF66] transition-all duration-500"
                            style={{ width: `${m.rate}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </Card>
                </div>

                {/* Recent redemptions */}
                <Card className="bg-[#111] border-white/10 overflow-hidden">
                  <div className="p-6 border-b border-white/5">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold">Recent Redemptions</div>
                  </div>
                  <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-[0.2em] text-white/40 font-bold border-b border-white/5">
                    <div className="col-span-2">Code</div>
                    <div className="col-span-3">Redeemed by</div>
                    <div className="col-span-3">Referrer</div>
                    <div className="col-span-2">Model</div>
                    <div className="col-span-1">Discount</div>
                    <div className="col-span-1 text-right">When</div>
                  </div>
                  {voucherAnalytics.recent.length === 0 ? (
                    <div className="p-8 text-center text-white/40 text-sm">No redemptions yet.</div>
                  ) : voucherAnalytics.recent.map((v) => (
                    <div key={v.id} className="grid grid-cols-12 px-4 py-3 border-b border-white/5 last:border-0 items-center text-sm" data-testid={`va-recent-${v.id}`}>
                      <div className="col-span-2 font-mono text-xs text-[#00FF66]">{v.code}</div>
                      <div className="col-span-3">
                        <div className="font-medium">{v.redeemed_by_name}</div>
                        <div className="text-xs text-white/50">{v.redeemed_by_phone}</div>
                      </div>
                      <div className="col-span-3 text-white/80">{v.user_name}</div>
                      <div className="col-span-2 text-white/60 text-xs">{v.redeemed_model || "—"}</div>
                      <div className="col-span-1 font-display font-bold text-[#00FF66]">₹{v.discount_inr}</div>
                      <div className="col-span-1 text-right text-xs text-white/40">
                        {v.redeemed_at ? new Date(v.redeemed_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
                      </div>
                    </div>
                  ))}
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="payouts">
            <Card className="bg-[#111] border-white/10 overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-[0.2em] text-white/40 font-bold border-b border-white/5">
                <div className="col-span-3">User</div>
                <div className="col-span-2">Amount</div>
                <div className="col-span-3">Bank</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {payouts.length === 0 ? (
                <div className="p-10 text-center text-white/40 text-sm" data-testid="admin-no-payouts">No payouts yet.</div>
              ) : payouts.map((p) => (
                <div key={p.id} className="grid grid-cols-12 px-4 py-3 border-b border-white/5 last:border-0 items-center text-sm" data-testid={`admin-payout-row-${p.id}`}>
                  <div className="col-span-3 font-medium">{p.user_name}</div>
                  <div className="col-span-2 font-display font-bold">{formatINR(p.amount)}</div>
                  <div className="col-span-3 text-xs text-white/60">
                    {p.bank_snapshot?.account_number} · {p.bank_snapshot?.ifsc}
                  </div>
                  <div className="col-span-2">
                    <Badge className="bg-white/10 text-white border-transparent">{p.status}</Badge>
                  </div>
                  <div className="col-span-2 text-right space-x-2">
                    {p.status === "requested" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => updatePayout(p.id, "approved")} className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid={`approve-payout-${p.id}`}>Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => updatePayout(p.id, "rejected")} className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid={`reject-payout-${p.id}`}>Reject</Button>
                      </>
                    )}
                    {p.status === "approved" && (
                      <Button size="sm" onClick={() => updatePayout(p.id, "paid")} className="bg-[#00FF66] text-black hover:bg-[#00E55C]" data-testid={`mark-paid-${p.id}`}>Mark Paid</Button>
                    )}
                  </div>
                </div>
              ))}
            </Card>
          </TabsContent>


          <TabsContent value="reels">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#00FF66] font-bold">Customer Reels</div>
                <div className="text-xs text-white/50 mt-1">30-second testimonials shown on landing</div>
              </div>
              <Button onClick={() => setReelDialog(true)} className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold" data-testid="add-reel-btn">
                <Plus className="w-4 h-4 mr-2" /> Add reel
              </Button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {reels.length === 0 ? (
                <div className="col-span-full p-10 text-center text-white/40 text-sm" data-testid="admin-no-reels">No reels yet.</div>
              ) : reels.map((r) => (
                <Card key={r.id} className="bg-[#111] border-white/10 overflow-hidden group" data-testid={`admin-reel-${r.id}`}>
                  <div className="aspect-[9/16] relative bg-black">
                    <img src={r.poster_url} alt={r.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                    <button
                      onClick={() => deleteReel(r.id)}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 hover:bg-red-500/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-reel-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <div className="text-[9px] uppercase tracking-[0.15em] text-[#00FF66] font-bold">{r.model_name}</div>
                      <div className="font-display font-bold text-sm text-white leading-tight mt-1">{r.title}</div>
                      <div className="text-[10px] text-white/60 mt-1">{r.customer_name}{r.city ? ` · ${r.city}` : ""}</div>
                    </div>
                  </div>
                  <div className="p-3 text-[10px] text-white/40 border-t border-white/5">
                    {r.video_url ? "🎬 Video linked" : "🖼️ Poster only"}
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="users">
            <Card className="bg-[#111] border-white/10 overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-[0.2em] text-white/40 font-bold border-b border-white/5">
                <div className="col-span-3">Name</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-2">Phone</div>
                <div className="col-span-2">Role</div>
                <div className="col-span-2">Referral Code</div>
              </div>
              {users.map((u) => (
                <div key={u.id} className="grid grid-cols-12 px-4 py-3 border-b border-white/5 last:border-0 items-center text-sm" data-testid={`admin-user-${u.id}`}>
                  <div className="col-span-3 font-medium">{u.name}</div>
                  <div className="col-span-3 text-white/70">{u.email}</div>
                  <div className="col-span-2 text-white/70">{u.phone}</div>
                  <div className="col-span-2 text-xs uppercase tracking-wider text-[#00FF66]">{u.role}</div>
                  <div className="col-span-2 font-mono text-white/70">{u.referral_code}</div>
                </div>
              ))}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!dialogRef} onOpenChange={(o) => !o && setDialogRef(null)}>
        <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Update Referral</DialogTitle>
          </DialogHeader>
          {dialogRef && (
            <div className="space-y-4">
              <div className="text-sm text-white/60">
                {dialogRef.referee_name} · {dialogRef.interested_model}
              </div>
              <div>
                <label className="text-xs text-white/60 uppercase tracking-[0.2em]">Status</label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="mt-2 bg-black/40 border-white/10" data-testid="admin-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-white/10 text-white">
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {newStatus === "purchased" && (
                <div>
                  <label className="text-xs text-white/60 uppercase tracking-[0.2em]">Reward amount (₹, optional)</label>
                  <Input value={rewardAmt} onChange={(e) => setRewardAmt(e.target.value)} placeholder="Auto by role" className="mt-2 bg-black/40 border-white/10" data-testid="admin-reward-input" />
                </div>
              )}
              <DialogFooter>
                <Button onClick={updateRef} className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold" data-testid="admin-update-save">Save changes</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>



      {/* Reel add dialog */}
      <Dialog open={reelDialog} onOpenChange={setReelDialog}>
        <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-lg" data-testid="reel-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Film className="w-5 h-5 text-[#00FF66]" /> New Reel</DialogTitle>
          </DialogHeader>
          <form onSubmit={addReel} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white/60 uppercase tracking-[0.2em]">Title</label>
                <Input required value={reelForm.title} onChange={(e) => setReelForm({ ...reelForm, title: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="reel-title" />
              </div>
              <div>
                <label className="text-xs text-white/60 uppercase tracking-[0.2em]">Customer name</label>
                <Input required value={reelForm.customer_name} onChange={(e) => setReelForm({ ...reelForm, customer_name: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="reel-customer" />
              </div>
              <div>
                <label className="text-xs text-white/60 uppercase tracking-[0.2em]">Model</label>
                <Input required value={reelForm.model_name} onChange={(e) => setReelForm({ ...reelForm, model_name: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="reel-model" />
              </div>
              <div>
                <label className="text-xs text-white/60 uppercase tracking-[0.2em]">City</label>
                <Input value={reelForm.city} onChange={(e) => setReelForm({ ...reelForm, city: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="reel-city" />
              </div>
            </div>
            <div>
              <label className="text-xs text-white/60 uppercase tracking-[0.2em]">Poster URL (vertical 9:16)</label>
              <Input required value={reelForm.poster_url} onChange={(e) => setReelForm({ ...reelForm, poster_url: e.target.value })} placeholder="https://..." className="mt-2 bg-black/40 border-white/10" data-testid="reel-poster" />
            </div>
            <div>
              <label className="text-xs text-white/60 uppercase tracking-[0.2em]">Video URL (optional, .mp4)</label>
              <Input value={reelForm.video_url} onChange={(e) => setReelForm({ ...reelForm, video_url: e.target.value })} placeholder="https://... .mp4" className="mt-2 bg-black/40 border-white/10" data-testid="reel-video" />
            </div>
            <div>
              <label className="text-xs text-white/60 uppercase tracking-[0.2em]">Quote (optional)</label>
              <Input value={reelForm.quote} onChange={(e) => setReelForm({ ...reelForm, quote: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="reel-quote" />
            </div>
            <DialogFooter>
              <Button type="submit" className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold" data-testid="reel-save">Save reel</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contest edit dialog */}
      <Dialog open={contestDialog} onOpenChange={setContestDialog}>
        <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-md" data-testid="contest-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit Contest</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-white/60 uppercase tracking-[0.2em]">Contest name</label>
              <Input value={contestForm.name} onChange={(e) => setContestForm({ ...contestForm, name: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="contest-name" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-white/60 uppercase tracking-[0.2em]">1st prize (₹)</label>
                <Input type="number" value={contestForm.prize1} onChange={(e) => setContestForm({ ...contestForm, prize1: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="contest-prize1" />
              </div>
              <div>
                <label className="text-xs text-white/60 uppercase tracking-[0.2em]">2nd (₹)</label>
                <Input type="number" value={contestForm.prize2} onChange={(e) => setContestForm({ ...contestForm, prize2: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="contest-prize2" />
              </div>
              <div>
                <label className="text-xs text-white/60 uppercase tracking-[0.2em]">3rd (₹)</label>
                <Input type="number" value={contestForm.prize3} onChange={(e) => setContestForm({ ...contestForm, prize3: e.target.value })} className="mt-2 bg-black/40 border-white/10" data-testid="contest-prize3" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={saveContest} className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold" data-testid="contest-save">Save</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Chat templates management dialog */}
      <Dialog open={templatesDialog} onOpenChange={setTemplatesDialog}>
        <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="templates-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Chat Templates</DialogTitle>
            <div className="text-xs text-white/50 mt-1">One-tap canned replies for faster support</div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 -mx-6 px-6">
            {chatTemplates.length === 0 && (
              <div className="text-sm text-white/40 py-6 text-center">No templates yet. Add your first below.</div>
            )}
            {chatTemplates.map((t) => (
              <div key={t.id} className="p-3 rounded-lg border border-white/10 bg-black/40 flex items-start justify-between gap-3" data-testid={`template-row-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-[#00FF66]">{t.label}</div>
                    <span className="text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded bg-white/10 text-white/60" data-testid={`template-cat-${t.id}`}>
                      {(TEMPLATE_CATEGORIES.find((c) => c.key === (t.category || "general")) || { label: "General" }).label}
                    </span>
                  </div>
                  <div className="text-xs text-white/60 mt-1 leading-relaxed break-words">{t.text}</div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => setTplForm({ id: t.id, label: t.label, text: t.text, category: t.category || "general" })}
                    className="w-8 h-8 rounded-full border border-white/10 hover:border-[#00FF66]/60 hover:text-[#00FF66] text-white/60 flex items-center justify-center transition-colors"
                    data-testid={`template-edit-${t.id}`}
                    aria-label="Edit"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => deleteTemplate(t.id)}
                    className="w-8 h-8 rounded-full border border-white/10 hover:border-red-500/60 hover:text-red-400 text-white/60 flex items-center justify-center transition-colors"
                    data-testid={`template-delete-${t.id}`}
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="pt-4 mt-2 border-t border-white/10 space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-bold">
              {tplForm.id ? "Edit template" : "Add new template"}
            </div>
            <Input
              value={tplForm.label}
              onChange={(e) => setTplForm({ ...tplForm, label: e.target.value })}
              placeholder="Short label (e.g. Payout on the way)"
              className="bg-black/40 border-white/10"
              maxLength={60}
              data-testid="template-label"
            />
            <textarea
              value={tplForm.text}
              onChange={(e) => setTplForm({ ...tplForm, text: e.target.value })}
              placeholder="Full message text sent to the customer"
              rows={3}
              className="w-full rounded-md bg-black/40 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-[#00FF66]/60"
              maxLength={2000}
              data-testid="template-text"
            />
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">Category</label>
              <Select value={tplForm.category} onValueChange={(v) => setTplForm({ ...tplForm, category: v })}>
                <SelectTrigger className="mt-1.5 bg-black/40 border-white/10 h-10 text-sm" data-testid="template-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-white/10 text-white">
                  {TEMPLATE_CATEGORIES.map((c) => (
                    <SelectItem key={c.key} value={c.key} data-testid={`template-cat-opt-${c.key}`}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              {tplForm.id && (
                <Button variant="outline" onClick={() => setTplForm({ id: null, label: "", text: "", category: "general" })} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="template-cancel-edit">
                  Cancel
                </Button>
              )}
              <Button onClick={saveTemplate} className="rounded-full bg-[#00FF66] text-black hover:bg-[#00E55C] font-semibold" data-testid="template-save">
                {tplForm.id ? "Update" : <><Plus className="w-4 h-4 mr-1" /> Add</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Chat attachment gallery dialog */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="gallery-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Images className="w-5 h-5" /> Thread Files
            </DialogTitle>
            <div className="text-xs text-white/50 mt-1">All photos, PDFs and voice notes shared in this conversation</div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2" data-testid="gallery-body">
            {threadAttachments.length === 0 ? (
              <div className="text-sm text-white/40 py-10 text-center">
                No files shared yet in this thread.
              </div>
            ) : (
              <>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-3">
                  {threadAttachments.length} file{threadAttachments.length === 1 ? "" : "s"}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {threadAttachments.slice().reverse().map((m) => (
                    <a
                      key={m.id}
                      href={attachmentSrc(m.attachment_url)}
                      target="_blank"
                      rel="noreferrer"
                      className="group rounded-lg overflow-hidden border border-white/10 bg-black/40 hover:border-[#00FF66]/40 transition-colors"
                      data-testid={`gallery-item-${m.id}`}
                    >
                      {m.attachment_type === "image" ? (
                        <div className="aspect-square bg-black/60 overflow-hidden">
                          <img src={attachmentSrc(m.attachment_url)} alt={m.attachment_name || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                      ) : m.attachment_type === "audio" ? (
                        <div className="aspect-square bg-gradient-to-br from-[#00FF66]/10 to-transparent flex flex-col items-center justify-center gap-2 p-3">
                          <Mic className="w-8 h-8 text-[#00FF66]" />
                          <div className="text-[10px] text-white/50 uppercase tracking-widest">Voice note</div>
                        </div>
                      ) : (
                        <div className="aspect-square bg-gradient-to-br from-white/5 to-transparent flex flex-col items-center justify-center gap-2 p-3">
                          <FileText className="w-8 h-8 text-white/70" />
                          <div className="text-[10px] text-white/50 uppercase tracking-widest">PDF</div>
                        </div>
                      )}
                      <div className="px-2.5 py-2 border-t border-white/5">
                        <div className="text-[11px] font-medium text-white truncate">{m.attachment_name || "Attachment"}</div>
                        <div className="text-[9px] text-white/40 mt-0.5 flex items-center justify-between gap-1">
                          <span>{m.from_role === "admin" ? "You" : m.from_name}</span>
                          <span>{new Date(m.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead timeline drawer */}
      <Dialog open={!!timelineLead} onOpenChange={(o) => !o && setTimelineLead(null)}>
        <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="timeline-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Buyer Journey</DialogTitle>
            {timelineLead && (
              <div className="text-xs text-white/60 mt-1">
                {timelineLead.name || "Anonymous"} · {timelineLead.phone || "—"} · {timelineLead.model_name}
              </div>
            )}
          </DialogHeader>
          <div className="overflow-y-auto py-4">
            {!timelineLead?.phone ? (
              <div className="text-center py-8 text-sm text-white/50">
                <Info className="w-6 h-6 mx-auto mb-3 text-white/30" />
                Timeline is only available for leads with a phone number.
              </div>
            ) : timelineEvents.length === 0 ? (
              <div className="text-center py-8 text-sm text-white/50">No events yet.</div>
            ) : (
              <div className="relative pl-8">
                <div className="absolute left-3 top-0 bottom-0 w-px bg-white/10" />
                {timelineEvents.map((e, idx) => (
                  <div key={e.id || idx} className="relative pb-6 last:pb-0" data-testid={`timeline-event-${idx}`}>
                    <div className="absolute -left-[26px] w-6 h-6 rounded-full bg-[#00FF66]/10 border border-[#00FF66]/40 flex items-center justify-center text-[#00FF66]">
                      {EVENT_ICONS[e.type] || <Info className="w-3 h-3" />}
                    </div>
                    <div className="font-semibold text-sm">{e.title}</div>
                    <div className="text-xs text-white/50 mt-0.5">
                      {new Date(e.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                    {e.meta && Object.keys(e.meta).length > 0 && (
                      <div className="mt-1.5 text-[10px] text-white/40 font-mono">
                        {Object.entries(e.meta).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
